import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { MediaSummary } from '@teaplus/shared';
import { api, errorMessage } from './api';
import { qk, useProfileId, useWatchlistIds } from './queries';

type Snap = Pick<MediaSummary, 'id' | 'type' | 'title' | 'posterUrl' | 'backdropUrl' | 'year'>;

export function useWatchlistToggle() {
  const qc = useQueryClient();
  const p = useProfileId();
  const ids = useWatchlistIds().data;
  const m = useMutation({
    mutationFn: async ({ media, add }: { media: Snap; add: boolean }) => {
      if (add) {
        await api('/api/watchlist', {
          body: {
            mediaId: media.id,
            contentType: media.type,
            snapshot: {
              title: media.title,
              posterUrl: media.posterUrl,
              backdropUrl: media.backdropUrl,
              year: media.year,
            },
          },
        });
      } else {
        await api(`/api/watchlist/${media.id}`, { method: 'DELETE' });
      }
    },
    onMutate: async ({ media, add }) => {
      await qc.cancelQueries({ queryKey: qk.watchlistIds(p) });
      const prev = qc.getQueryData<Set<string>>(qk.watchlistIds(p));
      const next = new Set(prev);
      if (add) next.add(media.id);
      else next.delete(media.id);
      qc.setQueryData(qk.watchlistIds(p), next);
      return { prev };
    },
    onError: (e, _v, c) => {
      qc.setQueryData(qk.watchlistIds(p), c?.prev);
      toast.error(errorMessage(e));
    },
    onSuccess: (_d, { media, add }) => {
      toast.success(
        add ? `Added ${media.title} to Watch Later` : `Removed ${media.title} from Watch Later`,
      );
      qc.invalidateQueries({ queryKey: qk.watchlist(p) });
      qc.invalidateQueries({ queryKey: qk.library(p) });
      qc.invalidateQueries({ queryKey: qk.home(p) });
    },
  });
  return {
    has: (id: string) => ids?.has(id) ?? false,
    toggle: (media: Snap) => m.mutate({ media, add: !(ids?.has(media.id) ?? false) }),
  };
}
